import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';
import { createSlug, LISTING_PAGE_PARAM_TYPE_EDIT, LISTING_PAGE_PARAM_TYPE_DRAFT } from '../../../../util/urlHelpers';

import { LISTING_STATE_PUBLISHED, LISTING_STATE_DRAFT } from '../../../../util/types';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = ({ location }) => {
  const to = !!location
    ? { state: { from: `${location.pathname}${location.search}${location.hash}` } }
    : {};
  return (
    <NamedLink name="LoginPage" className={classNames(css.topbarLink, css.loginLink)} to={to}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const AboutUsLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'about' }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.about" />
      </span>
    </NamedLink>
  );
};

const TutorLink = () => {
  return (
    <NamedLink name="tutorPage" params={{ pageId: 'tutor' }} className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.tutor" />
      </span>
    </NamedLink>
  );
};

const ContactUsLink = () => {
  return (
    <NamedLink
      name="CMSPage"
      params={{ pageId: 'contact-us' }}
      className={classNames(css.topbarLink, css.contactUsLink)}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.contact" />
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  currentUserHasListings,
  currentUserListing,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const isApproved =
    currentUserListing?.id && currentUserListing?.attributes?.state === LISTING_STATE_PUBLISHED;

  const currentUserListingTitle = currentUserListing?.attributes?.title;
  const currentUserListingId = currentUserListing?.id?.uuid;
  const slug = currentUserListingTitle && createSlug(currentUserListingTitle);
  const isDraft = currentUserListing?.attributes?.state === LISTING_STATE_DRAFT;
  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  isDraft

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          currentUserHasListings && !!currentUserListingId && !!currentUserListingTitle ? (
            <MenuItem key="EditListingPage">
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('EditListingPage'))}
                name="EditListingPage"
                params={{
                  id: currentUserListingId,
                  slug,
                  type: editListingLinkType,
                  tab: 'details',
                }}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.yourListingLink" />
              </NamedLink>
            </MenuItem>
          ) : (
            <MenuItem key="NewListingsPage">
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('EditListingPage'))}
                name="EditListingPage"
                params={{
                  slug: 'draft',
                  id: '00000000-0000-0000-0000-000000000000',
                  type: 'new',
                  tab: 'details',
                }}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.createListingLink" />
              </NamedLink>
            </MenuItem>
          )
        ) : null}
        {isApproved &&
        showManageListingsLink &&
        currentUserHasListings &&
        !!currentUserListingId &&
        !!currentUserListingTitle ? (
          <MenuItem key="ListingPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ListingPage'))}
              name="ListingPage"
              params={{
                id: currentUserListingId,
                slug,
              }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.viewYourListingLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    currentUserHasListings,
    currentUserListing,
    location,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      currentUserHasListings={currentUserHasListings}
      currentUserListing={currentUserListing}
    />
  ) : null;

  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink location={location} />;

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />
      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        showCreateListingsLink={showCreateListingsLink}
      />
      <TutorLink />
      <AboutUsLink />
      <ContactUsLink />
      {inboxLinkMaybe}
      {profileMenuMaybe}
      {loginLinkMaybe}
    </nav>
  );
};

export default TopbarDesktop;
